import { Injectable, inject } from '@angular/core';
import { Interval } from 'date-fns';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { assert } from '../../../../../../core/assert/assert';
import { DayOrWeekCalendarModel } from '../../../../../../core/models/calendar/day-or-week-calendar.model';
import { MonthCalendarModel } from '../../../../../../core/models/calendar/month-calendar.model';
import { TimeBlockModel } from '../../../../../../core/models/timeblock/time-block.model';
import { TimeBlockFilterService } from '../../../../../../core/services/features/time-tracking/time-block-filter.service';
import { WorkingTimeService } from '../../../../../../core/services/features/working-time/working-time.service';
import { UserService } from '../../../../../../pages/user/services/user.service';
import { fillDateMap } from '../../../../../../shared/functions/map-functions';
import { DefaultDateFormat } from '../../../../../../shared/localization/formats';
import { CalendarEvents, CalendarViews } from '../../../../calendar-types';
import { CalendarService } from '../../../../services/calendar.service';
import { DateTimeHelper } from '../../../../util/date-time-helper';
import { TimeBlockItemBuilderService } from '../../generation/time-block-item-builder.service';
import { TimeBlockStructureService } from '../../time-block-structure/time-block-structure.service';
import { ITimeBlockComponentItem } from '../../types/time-block-component-item-types';

@Injectable()
export class TimeBlockDurationCalculationService {
  public calculateTimeBlockDurationSums$ = new Subject();

  private readonly calendarService = inject(CalendarService);
  private readonly timeBlockStructureService = inject(TimeBlockStructureService);
  private readonly timeBlockItemBuilderService = inject(TimeBlockItemBuilderService);
  private readonly workingTimeService = inject(WorkingTimeService);
  private readonly userService = inject(UserService);

  public static calculateTimeBlockDurationWithinDay(timeBlockModel: TimeBlockModel): number {
    const start = new Date(timeBlockModel.start);
    const end = new Date(
      DateTimeHelper.transformToExactlyMidnightIfNeeded(timeBlockModel.end, true),
    );
    if (DateTimeHelper.isBefore(end, start)) {
      throw new Error('End date is before start date.');
    }

    // Don't truncate seconds for active time block. The time block dialog start / end times would not be in sync
    // with the end time of the active time block otherwise.
    if (!timeBlockModel.isActive) {
      start.setSeconds(0);
      end.setSeconds(0);
    }

    return DateTimeHelper.differenceInSeconds(end, start);
  }

  public initEvents(): void {
    this.calculateTimeBlockDurationSums$.subscribe(() => {
      this.calculateDailyTrackedTimeSum();
    });
  }

  public getTimeInRangeForTimeBlocks(
    timeBlocks: readonly ITimeBlockComponentItem[],
    timeRange: Interval = {
      start: null,
      end: null,
    },
  ): number {
    return timeBlocks.reduce((accum: number, current: ITimeBlockComponentItem) => {
      const clone = current.clone(this.timeBlockItemBuilderService);
      return accum + this.getTimeBlockTimeInSeconds(clone, timeRange);
    }, 0);
  }

  public getTimeBlockTimeInSeconds(
    timeBlock: ITimeBlockComponentItem,
    timeRange: Interval = {
      start: null,
      end: null,
    },
  ) {
    if (timeBlock.timeBlockModel.fixInterval === null) {
      return this.getTimeBlockTimeInRangeVariableInterval(timeBlock, timeRange);
    }
    return this.getTimeBlockTimeInRangeFixInterval(timeBlock, timeRange);
  }

  public removeOverlapsForDays(
    timeBlockMap: Map<string, ITimeBlockComponentItem[]>,
  ): Map<string, ITimeBlockComponentItem[]> {
    const overlapFreeTimeBlockMap = new Map<string, ITimeBlockComponentItem[]>();
    for (const [dateString, timeBlocks] of timeBlockMap) {
      overlapFreeTimeBlockMap.set(dateString, new Array<ITimeBlockComponentItem>());
      const overlapFreeTimeBlocks = this.removeOverlaps(timeBlocks);
      overlapFreeTimeBlockMap.set(dateString, overlapFreeTimeBlocks);
    }
    return overlapFreeTimeBlockMap;
  }

  public removeOverlaps(timeBlocks: ITimeBlockComponentItem[]): ITimeBlockComponentItem[] {
    if (!Array.isArray(timeBlocks) || timeBlocks.length === 0) {
      return [];
    }

    // 1) Sort: by start asc; if start equal, shorter first
    const sortedVariableIntervalTimeBlocks = timeBlocks
      .filter((tb) => tb.timeBlockModel.fixInterval === null)
      .sort((timeBlock1, timeBlock2) => {
        const startTime =
          timeBlock1.timeBlockModel.start.getTime() - timeBlock2.timeBlockModel.start.getTime();
        if (startTime === 0) {
          const durationA =
            timeBlock1.timeBlockModel.end.getTime() - timeBlock1.timeBlockModel.end.getTime();
          const durationB =
            timeBlock2.timeBlockModel.end.getTime() - timeBlock2.timeBlockModel.end.getTime();
          return durationA - durationB;
        }
        return startTime;
      });

    let prevTimeBlock: ITimeBlockComponentItem = null;
    const overlapFreeTimeBlocks = [];
    sortedVariableIntervalTimeBlocks.forEach((timeBlock) => {
      if (prevTimeBlock === null) {
        prevTimeBlock = timeBlock;
        overlapFreeTimeBlocks.push(timeBlock);
      } else {
        // if they are intersecting
        if (timeBlock.timeBlockModel.end.getTime() > prevTimeBlock.timeBlockModel.end.getTime()) {
          if (timeBlock.timeBlockModel.start < prevTimeBlock.timeBlockModel.end) {
            timeBlock.timeBlockModel.start = prevTimeBlock.timeBlockModel.end;
          }
          overlapFreeTimeBlocks.push(timeBlock);
          prevTimeBlock = timeBlock;
        }
      }
    });

    const fixedIntervalTimeBlocks = timeBlocks.filter(
      (tb) => tb.timeBlockModel.fixInterval !== null,
    );

    return [...overlapFreeTimeBlocks, ...fixedIntervalTimeBlocks];
  }

  public calculateTimeBlockTimeSum(
    timeBlocks: readonly ITimeBlockComponentItem[],
    excludeOverlappingTime: boolean,
    timeRange: Interval = {
      start: null,
      end: null,
    },
  ): number {
    const accumulatedTimeMap = this.calculateTimePerDay(
      timeBlocks,
      excludeOverlappingTime,
      timeRange,
    );
    let timeSum = 0;
    for (const [, timePerDay] of accumulatedTimeMap) {
      timeSum += timePerDay;
    }
    return timeSum;
  }

  /**
   * Trims the time block map by removing entries where the key (date) is not within the specified time range.
   * @param timeBlockMapPerDay - The map of time blocks with dates as keys.
   * @param timeRange - The time range to filter the map by.
   * @returns The trimmed map of time blocks.
   */
  public trimTimeBlockTimes(
    timeBlockMapPerDay: Map<string, ITimeBlockComponentItem[]>,
    timeRange: Interval,
  ): Map<string, ITimeBlockComponentItem[]> {
    for (const key of timeBlockMapPerDay.keys()) {
      const date = DateTimeHelper.parse(key, DefaultDateFormat, new Date());

      if (!DateTimeHelper.isDateInOpenInterval(date, timeRange)) {
        timeBlockMapPerDay.delete(key);
      }
    }
    return timeBlockMapPerDay;
  }

  public createTimeBlockMapPerDay(
    timeBlocks: readonly ITimeBlockComponentItem[],
    excludeOverlappingTime: boolean,
    timeRange: Interval,
  ) {
    const timeBlockMapPerDay = this.insertTimeBlocksIntoMap(timeBlocks);
    const splitTimeBlockMapPerDay = this.truncateTimeBlockTimes(timeBlockMapPerDay);

    let trimmedTimeBlockMapPerDay = this.trimTimeBlockTimes(splitTimeBlockMapPerDay, timeRange);

    if (excludeOverlappingTime) {
      const trimmedTimeBlockMapPerDayTemp = new Map<string, ITimeBlockComponentItem[]>();
      this.groupTimeBlockMapPerDayByUserId(trimmedTimeBlockMapPerDay).forEach(
        (timeBlockMapPerUser) => {
          const overlapFreeTimeBlockMapPerUser = this.removeOverlapsForDays(timeBlockMapPerUser);
          // Merge back into trimmedTimeBlockMapPerDay
          for (const [dateString, timeBlocks] of overlapFreeTimeBlockMapPerUser) {
            if (!trimmedTimeBlockMapPerDayTemp.has(dateString)) {
              trimmedTimeBlockMapPerDayTemp.set(dateString, []);
            }
            trimmedTimeBlockMapPerDayTemp.get(dateString).push(...timeBlocks);
          }
        },
      );
      trimmedTimeBlockMapPerDay = trimmedTimeBlockMapPerDayTemp;
    }
    return trimmedTimeBlockMapPerDay;
  }

  public calculateTimePerDay(
    timeBlocks: readonly ITimeBlockComponentItem[],
    excludeOverlappingTime: boolean,
    timeRange: Interval = {
      start: null,
      end: null,
    },
  ): Map<string, number> {
    const splitTimeBlockMapPerDay = this.createTimeBlockMapPerDay(
      timeBlocks,
      excludeOverlappingTime,
      timeRange,
    );
    const totalTimePerDay = new Map<string, number>();
    for (const [dateString, timeBlocks] of splitTimeBlockMapPerDay) {
      totalTimePerDay.set(dateString, this.getTimeInRangeForTimeBlocks(timeBlocks));
    }
    return totalTimePerDay;
  }

  public accumulateByWeek(accumulatedTimeMap: Map<string, number>) {
    const accumulatedTimeMapPerWeek = new Map<string, number>();
    for (const [dateString, time] of accumulatedTimeMap) {
      const date = DateTimeHelper.parse(dateString, DefaultDateFormat, new Date());
      const key = DateTimeHelper.weekNumber(date).toString();
      if (accumulatedTimeMapPerWeek.get(key) === undefined) {
        accumulatedTimeMapPerWeek.set(key, 0);
      }
      accumulatedTimeMapPerWeek.set(key, accumulatedTimeMapPerWeek.get(key) + time);
    }
    return accumulatedTimeMapPerWeek;
  }

  public insertTimeBlocksIntoMap(timeBlocks: readonly ITimeBlockComponentItem[]) {
    const dateMap = new Map<string, ITimeBlockComponentItem[]>();

    timeBlocks.forEach((timeBlock) => {
      let end = timeBlock.timeBlockModel.end;
      if (timeBlock.timeBlockModel.fixInterval === null) {
        end = DateTimeHelper.transformToNearlyMidnightIfNeeded(end);
      }

      DateTimeHelper.eachDayOfInterval(timeBlock.timeBlockModel.start, end).forEach((date) => {
        const key = DateTimeHelper.format(date, DefaultDateFormat);
        let timeBlockList = dateMap.get(key);
        if (timeBlockList === undefined) {
          timeBlockList = new Array<ITimeBlockComponentItem>();
          dateMap.set(key, timeBlockList);
        }
        timeBlockList.push(timeBlock);
      });
    });
    return dateMap;
  }

  /**
   * Truncate start and end times (i.e. set to 00:00:00) if time blocks pass midnight.
   * @param timeBlockMap - The map of dates to time blocks to split.
   * @returns A map of dates to split time blocks.
   */
  public truncateTimeBlockTimes(
    timeBlockMap: Map<string, ITimeBlockComponentItem[]>,
  ): Map<string, ITimeBlockComponentItem[]> {
    const truncatedTimeBlockMap = new Map<string, ITimeBlockComponentItem[]>();
    for (const [dateString, timeBlocks] of timeBlockMap) {
      truncatedTimeBlockMap.set(dateString, new Array<ITimeBlockComponentItem>());

      timeBlocks.forEach((timeBlock) => {
        const timeBlockClone = timeBlock.clone(this.timeBlockItemBuilderService);
        timeBlockClone.timeBlockModel.start = this.truncateStartIfNeeded(
          timeBlock,
          DateTimeHelper.parseISO(dateString),
        );

        timeBlockClone.timeBlockModel.end = this.truncateEndIfNeeded(
          timeBlock,
          DateTimeHelper.parseISO(dateString),
        );
        truncatedTimeBlockMap.get(dateString).push(timeBlockClone);
      });
    }
    return truncatedTimeBlockMap;
  }

  // Note: The time is only relevant for variable interval time blocks
  private truncateEndIfNeeded(timeBlock: ITimeBlockComponentItem, curDate: Date): Date {
    if (timeBlock.timeBlockModel.end.getDate() !== curDate.getDate()) {
      curDate.setHours(0, 0, 0, 0);
      if (timeBlock.timeBlockModel.fixInterval !== null) {
        return curDate;
      }
      return DateTimeHelper.addDays(curDate, 1);
    }
    return timeBlock.timeBlockModel.end;
  }

  // Note: The time is only relevant for variable interval time blocks
  private truncateStartIfNeeded(timeBlock: ITimeBlockComponentItem, curDate: Date): Date {
    if (timeBlock.timeBlockModel.start.getDate() !== curDate.getDate()) {
      curDate.setHours(0, 0, 0, 0);
      return curDate;
    }
    return timeBlock.timeBlockModel.start;
  }

  private calculateDailyTrackedTimeSum(): void {
    const calendarView = this.calendarService.model.calendarViewMode;

    if (calendarView === CalendarViews.DayGrid || calendarView === CalendarViews.WeekGrid) {
      // day or week view
      const fixIntervalCalendarModel = (this.calendarService.model as DayOrWeekCalendarModel)
        .fixIntervalCalendarModel;

      // inner day
      const visibleRange: Interval = {
        start: this.calendarService.model.calendarProperties.visibleStartDate,
        end: this.calendarService.model.calendarProperties.visibleEndDate,
      };

      const variableIntervalTimeBlocks = this.timeBlockStructureService.getTimeBlockMap(
        false,
        visibleRange,
      );
      const fixIntervalTimeBlocks = fixIntervalCalendarModel.timeBlocks;

      const mergedTimeBlocks: ITimeBlockComponentItem[] = [
        ...this.timeBlockMapToArray(variableIntervalTimeBlocks),
        ...this.timeBlockMapToArray(fixIntervalTimeBlocks),
      ];

      const workingTimeBlocks =
        TimeBlockFilterService.removeNonWorkingTimeTimeBlocks(mergedTimeBlocks);
      const accumulatedTimeMap = this.calculateTimePerDay(workingTimeBlocks, true);
      const updatedTimeMap = fillDateMap(accumulatedTimeMap, visibleRange);
      this.calendarService.emitCalendarChange(
        this.calendarService.model,
        CalendarEvents.CalculatedWorkingTimeDurations,
        updatedTimeMap,
      );
    } else {
      // month view
      const calendarModel = (this.calendarService.model as MonthCalendarModel)
        .fixIntervalCalendarModel;

      const visibleRange: Interval = {
        start: calendarModel.calendarProperties.visibleStartDate,
        end: calendarModel.calendarProperties.visibleEndDate,
      };

      const fixIntervalTimeBlocks = this.timeBlockStructureService.getTimeBlockMap(
        true,
        visibleRange,
      );
      const variableIntervalTimeBlocks = this.timeBlockStructureService.getTimeBlockMap(
        false,
        visibleRange,
      );

      const mergedTimeBlocks = [
        ...this.timeBlockMapToArray(variableIntervalTimeBlocks),
        ...this.timeBlockMapToArray(fixIntervalTimeBlocks),
      ];

      const workingTimeBlocks =
        TimeBlockFilterService.removeNonWorkingTimeTimeBlocks(mergedTimeBlocks);
      const accumulatedTimeMap = this.calculateTimePerDay(workingTimeBlocks, true);
      const updatedTimeMap = fillDateMap(accumulatedTimeMap, visibleRange);
      const accumulatedTimeMapPerWeek = this.accumulateByWeek(updatedTimeMap);

      this.calendarService.emitCalendarChange(
        this.calendarService.model,
        CalendarEvents.CalculatedWorkingTimeDurations,
        accumulatedTimeMapPerWeek,
      );
    }
  }

  private timeBlockMapToArray(
    timeBlocksMap: Map<string, ITimeBlockComponentItem[]>,
  ): ITimeBlockComponentItem[] {
    const result: ITimeBlockComponentItem[] = [];

    for (const timeBlocks of timeBlocksMap.values()) {
      timeBlocks.forEach((timeBlock) => {
        // don't add duplicates
        if (!result.find((timeBlock_) => timeBlock_.id === timeBlock.id)) {
          result.push(timeBlock);
        }
      });
    }
    return result;
  }

  /**
   * Calculates the total time in seconds for the given time blocks in the given time range. Assumes that the time block
   * has been trimmed already.
   */
  private getTimeBlockTimeInRangeVariableInterval(
    timeBlock: ITimeBlockComponentItem,
    timeRange: Interval,
  ): number {
    // Parse the interval start and end into Date objects (handle null cases)
    const intervalStart = timeRange.start ? (timeRange.start as Date) : null;
    const intervalEnd = timeRange.end ? (timeRange.end as Date) : null;

    // Determine the actual overlap start and end
    const overlapStart = intervalStart
      ? new Date(Math.max(timeBlock.timeBlockModel.start.getTime(), intervalStart.getTime()))
      : timeBlock.timeBlockModel.start;
    const overlapEnd = intervalEnd
      ? new Date(Math.min(timeBlock.timeBlockModel.end.getTime(), intervalEnd.getTime()))
      : timeBlock.timeBlockModel.end;

    // Calculate the overlap in seconds
    const overlapTime = DateTimeHelper.differenceInSeconds(overlapEnd, overlapStart);

    // If there is no overlap, return 0
    if (overlapTime <= 0) {
      return 0;
    }

    return overlapTime;
  }

  private getTimeBlockTimeInRangeFixInterval(
    timeBlock: ITimeBlockComponentItem,
    timeRange: Interval,
  ): number {
    let fixIntervalTime = 0;
    if (DateTimeHelper.isDateInOpenInterval(timeBlock.timeBlockModel.start, timeRange)) {
      const user$ = this.userService.getUserEnsured(timeBlock.timeBlockModel.userId);
      user$.pipe(take(1)).subscribe((user) => {
        assert(typeof user !== 'undefined', 'User is not defined.');
        fixIntervalTime = this.workingTimeService.getWorkingTime(
          timeBlock.timeBlockModel.start,
          user,
          timeBlock.timeBlockModel.fixInterval,
        );
      });
    }
    return fixIntervalTime;
  }

  private groupTimeBlockMapPerDayByUserId(
    trimmedTimeBlockMapPerDay: Map<string, ITimeBlockComponentItem[]>,
  ): Map<number, Map<string, ITimeBlockComponentItem[]>> {
    const mapPerUser = new Map<number, Map<string, ITimeBlockComponentItem[]>>();

    // Iterate over all dates
    trimmedTimeBlockMapPerDay.forEach((timeBlocks, dateString) => {
      timeBlocks.forEach((timeBlock) => {
        const userId = timeBlock.timeBlockModel.userId;

        // Ensure we have a map for this user
        if (!mapPerUser.has(userId)) {
          mapPerUser.set(userId, new Map<string, ITimeBlockComponentItem[]>());
        }

        const userMap = mapPerUser.get(userId);

        // Ensure we have a list for this date for that user
        if (!userMap.has(dateString)) {
          userMap.set(dateString, []);
        }

        userMap.get(dateString).push(timeBlock);
      });
    });

    return mapPerUser;
  }
}
